import { Plus, Target, Trophy, Mail, ToggleLeft, ToggleRight } from "lucide-react";
import PropTypes from "prop-types";
import DealerMismatchWidget from "./DealerMismatchWidget";
import DealerManualLangBadge from "./DealerManualLangBadge";

const DealerDashboardView = ({
  stats, dealerRole, setView,
  globalTarget, leaderboard,
  digestEnabled, digestLoading, sendingPreview, toggleDigest, sendDigestPreview, t,
  dealerToken, dealerCountry
}) => {
  // Native-language manual badge is always rendered — even when stats
  // are still loading — because for new dealers (zero customers) the
  // dashboard body is empty and this is precisely the moment when we
  // most want them to discover the operational guide.
  if (!stats) {
    return (
      <div className="animate-fade-in">
        <DealerManualLangBadge dealerCountry={dealerCountry} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: t.totalCustomers, value: stats.total_customers, color: 'text-white' },
          { label: t.emailsSent, value: stats.emails_sent, color: 'text-blue-400' },
          { label: t.termsAccepted, value: stats.terms_accepted, color: 'text-lojack-clause-400' },
          { label: t.contractsSigned, value: stats.contracts_signed, color: 'text-lojack-blue-400' },
          { label: t.cardsCreated, value: stats.cards_created, color: 'text-purple-400' },
          { label: t.last30days, value: stats.last_30_days, color: 'text-lojack-red' },
        ].map(s => (<div key={s.label} className="wallet-card text-center"><p className={`text-2xl font-bold ${s.color} tabular-nums`}>{s.value}</p><p className="text-gray-500 text-xs mt-1">{s.label}</p></div>))}
      </div>
      <div className="wallet-card text-center py-8">
        <Plus size={40} className="text-gray-700 mx-auto mb-3" /><p className="text-gray-400">{t.startAdding}</p>
        <button onClick={() => setView('new')} className="btn-lojack mt-4" data-testid="dealer-goto-new">{t.newCustomer}</button>
      </div>

      {/* Native-language operational manual discovery aid (hidden for IT) */}
      <DealerManualLangBadge dealerCountry={dealerCountry} />

      {/* Country mismatch quality feedback */}
      <DealerMismatchWidget dealerToken={dealerToken} t={t} />

      {/* Seller's own target progress */}
      {dealerRole === 'seller' && stats?.monthly_target > 0 && (
        <div className="wallet-card mt-4" data-testid="seller-target-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target size={16} className="text-lojack-red" />
              <p className="text-white text-sm font-medium">{t.monthlyTarget}</p>
            </div>
            <p className="text-white font-bold tabular-nums">{stats.monthly_progress}/{stats.monthly_target}</p>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${
              stats.monthly_progress >= stats.monthly_target ? 'bg-lojack-blue-500' :
              stats.monthly_progress >= stats.monthly_target * 0.7 ? 'bg-lojack-clause-500' : 'bg-lojack-red'
            }`} style={{width: `${Math.min(100, (stats.monthly_progress / stats.monthly_target) * 100)}%`}} data-testid="seller-progress-bar" />
          </div>
          <p className="text-gray-500 text-xs mt-2">
            {stats.monthly_progress >= stats.monthly_target
              ? t.targetReached
              : `${stats.monthly_target - stats.monthly_progress} ${t.remainingClients}`}
          </p>
        </div>
      )}

      {/* Seller's leaderboard mini-view */}
      {dealerRole === 'seller' && leaderboard && leaderboard.top3 && leaderboard.top3.length > 0 && (
        <div className="wallet-card mt-4" data-testid="seller-leaderboard-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="text-lojack-clause-400" />
              <p className="text-white text-sm font-medium">{t.leaderboard} {leaderboard.month}</p>
            </div>
            {leaderboard.my_position && (
              <span className="text-sm font-bold text-lojack-red" data-testid="seller-my-position">
                {t.youAreAt} {leaderboard.my_position}° {t.place}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {leaderboard.top3.map((s, i) => {
              const medals = [
                { bg: 'bg-lojack-clause-500/10', border: 'border-lojack-clause-500/30', text: 'text-lojack-clause-400', label: '1°' },
                { bg: 'bg-gray-400/10', border: 'border-gray-400/30', text: 'text-gray-300', label: '2°' },
                { bg: 'bg-lojack-clause-700/10', border: 'border-lojack-clause-700/30', text: 'text-lojack-clause-600', label: '3°' },
              ];
              const m = medals[i];
              const isMe = leaderboard.my_stats && s.id === leaderboard.my_stats.id;
              return (
                <div key={s.id} className={`flex items-center justify-between py-2.5 px-3 rounded-lg ${isMe ? 'bg-lojack-red/10 border border-lojack-red/30' : 'bg-[#111] border border-gray-800'}`}
                  data-testid={`seller-lb-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full ${m.bg} border ${m.border} flex items-center justify-center text-xs font-bold ${m.text}`}>{m.label}</span>
                    <span className={`text-sm font-medium ${isMe ? 'text-white' : 'text-gray-300'}`}>
                      {s.first_name} {s.last_name} {isMe && <span className="text-lojack-red text-xs ml-1">({t.you})</span>}
                    </span>
                  </div>
                  <span className="text-white font-bold tabular-nums text-sm">{s.customers_this_month} <span className="text-gray-500 font-normal text-xs">{t.clients}</span></span>
                </div>
              );
            })}
            {leaderboard.my_position && leaderboard.my_position > 3 && leaderboard.my_stats && (
              <>
                <div className="text-center text-gray-600 text-xs py-1">...</div>
                <div className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-lojack-red/10 border border-lojack-red/30"
                  data-testid="seller-lb-my-row">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-lojack-red/20 border border-lojack-red/40 flex items-center justify-center text-xs font-bold text-lojack-red">{leaderboard.my_position}°</span>
                    <span className="text-sm font-medium text-white">{leaderboard.my_stats.first_name} {leaderboard.my_stats.last_name} <span className="text-lojack-red text-xs ml-1">({t.you})</span></span>
                  </div>
                  <span className="text-white font-bold tabular-nums text-sm">{leaderboard.my_stats.customers_this_month} <span className="text-gray-500 font-normal text-xs">{t.clients}</span></span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Digest settings (dealer only) */}
      {dealerRole === 'dealer' && (
        <div className="wallet-card mt-4 flex items-center justify-between" data-testid="digest-settings-card">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <Mail size={16} className="text-blue-400" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">{t.weeklyDigest}</p>
              <p className="text-gray-500 text-xs">{t.digestDescription}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={sendDigestPreview} disabled={sendingPreview}
              className="text-xs text-gray-400 hover:text-white border border-gray-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50" data-testid="digest-preview-btn">
              {sendingPreview ? t.sending : t.sendPreview}
            </button>
            <button onClick={toggleDigest} disabled={digestLoading}
              className="transition-colors disabled:opacity-50" data-testid="digest-toggle">
              {digestEnabled
                ? <ToggleRight size={28} className="text-lojack-blue-400" />
                : <ToggleLeft size={28} className="text-gray-600" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealerDashboardView;

DealerDashboardView.propTypes = {
  stats: PropTypes.object,                   // null while loading — guarded by `if (!stats) return null;`
  dealerRole: PropTypes.string,              // 'dealer' | 'seller'
  setView: PropTypes.func.isRequired,
  globalTarget: PropTypes.object,
  leaderboard: PropTypes.object,
  digestEnabled: PropTypes.bool,
  digestLoading: PropTypes.bool,
  sendingPreview: PropTypes.bool,
  toggleDigest: PropTypes.func,
  sendDigestPreview: PropTypes.func,
  t: PropTypes.object.isRequired,            // translation pack — required, app cannot render labels without it
  dealerToken: PropTypes.string.isRequired,  // forwarded to DealerMismatchWidget for scoped API calls
  dealerCountry: PropTypes.string,           // ISO country code of the logged-in dealer — used by the native-lang manual badge; undefined/IT hides the badge
};
